import { DataSTag, Sel, STagNew } from '#~/stag/index.js';
import { Button } from '@studio/design-system/button';
import { Notice as _Notice } from '@studio/design-system/notification';
import { isNil, isNotNil } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useEffect } from 'react';
import type { Props } from './types.js';
import { acted } from '../../utils.js';

/** `<Notice>` wrapper. */
export const Notice = ({
  actions,
  announce,
  autoClose,
  notification,
  onShown,
  ...props
}: Props): ReactElement => {
  const { id, severity } = notification;

  // Store delivery can happen behind a blocking prompt. Only a mounted notice
  // reports visibility; the incident helper deduplicates remounts of that showing.
  useEffect(() => {
    onShown?.();
  }, [id, onShown]);

  return (
    <_Notice
      // Every posted toast is addressable by its notification id. Callers that
      // a test must find (e.g. the egress codec refusal) pass a PINNED id, so
      // the spec builds the same tag instead of matching on the copy. Per rule:
      // playwright-stag-selectors-only (ai.knowledge/platform).
      {...DataSTag(Sel.Notifications, 'notice', String(id))}
      // Corner toasts float over the room's controls, so they keep their full,
      // content-sized layout; the clamped standard size is for the bell tray list.
      collapsible={false}
      {...props}
      // Real, keyboard-reachable buttons under the message (ENG-4930 item 3).
      // Pressing one reports the action before running it; closing stays with
      // the caller. Per rule: keyboard-reachable-interactive-elements.
      actions={
        actions && actions.length > 0 ? (
          <div className='flex flex-row flex-wrap gap-8'>
            {actions.map(({ action, label, onClick, variant = 'secondary' }) => (
              <Button
                key={action}
                {...STagNew(Sel.Notifications, 'notice.action', `${String(id)}.${action}`)}
                onClick={(): void => {
                  acted(id, action);
                  onClick();
                }}
                size='sm'
                variant={variant}
              >
                {label}
              </Button>
            ))}
          </div>
        ) : undefined
      }
      // Red interrupts a screen reader; yellow, green and unlabelled wait for a
      // pause (ENG-4930 item 4). Callers may still set `announce` themselves.
      announce={isNotNil(announce) ? announce : severity === 'error' ? 'assertive' : 'polite'}
      autoClose={
        isNotNil(autoClose)
          ? autoClose
          : isNil(severity) || ['info', 'success'].includes(severity)
            ? true
            : undefined
      }
      notification={notification}
    />
  );
};
